import { Page, View, Text, Image } from "@react-pdf/renderer";
import { getCurrencyDisplayLabel } from "../../utils/currencyDisplay";
import { formatExchangeValue } from "../../utils/formatExchangeValue";
import { add, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { styles } from "./styles";
import { ServiceOptions } from "../../utils/serviceTypesFromBudget";
import { BudgetItemTypeEnum } from "../../types/budget/budget";
import { isPfizerClient } from "../../constants/pfizerClient";

const formatter = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const CalcOptionLabels: { [key: string]: any } = {
  A: "[A] Pagamento direto pelo cliente (Cartão de Crédito, EBTA ou PO direta)",
  B: "[B] Nota de débito (faturados contra o cliente por meio de Nota de Débito MM)",
  C: "[C] Custos de terceiros (faturados contra a MM e cobrados do cliente em Nota Fiscal MM)",
  D: "[D] Custos internos (cobrados do cliente em Nota Fiscal MM – sem honorários)",
};

export const formatCurrency = (value: number) => {
  if (!value) return "0,00";
  return formatter.format(value);
};

function formatDate(date: string) {
  return !isNaN(new Date(date).getTime())
    ? format(add(new Date(date), { hours: 4 }), "dd/MM/yyyy")
    : "-";
}

const groupByAttributes = (array: any[], keys: any) => {
  const groups: any = {};

  array.forEach((item) => {
    const key = keys.map((k: any) => item[k]).join("|");

    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(item);
  });

  return groups;
};

const convertToNumber = (str: any) => {
  return parseFloat(str.replace(/[^\d,-]/g, "").replace(",", "."));
};

const calculateTotals = (data: any) => {
  let totalTariff = 0;
  let totalCostInCurrency = 0;
  let totalCostInBRL = 0;

  data.forEach((item: any) => {
    const row = item.row;

    totalTariff += convertToNumber(row[8]);
    totalCostInCurrency += convertToNumber(row[9]);
    totalCostInBRL += convertToNumber(row[10]);
  });

  return {
    tariffTotal: totalTariff,
    totalCostInCurrency: totalCostInCurrency,
    totalCostInBRL: totalCostInBRL,
  };
};

function getUniqueKeys<T extends { key: string }>(arr: T[][]): string[] {
  return Array.from(
    new Set(arr.flatMap((innerArray) => innerArray.map((item) => item.key)))
  );
}

function getUniqueValues<T>(array: T[]): T[] {
  return Array.from(new Set(array));
}

function divideInPortions<T>(array: T[], tamanho: number = 6): T[][] {
  const resultado: T[][] = [];
  for (let i = 0; i < array.length; i += tamanho) {
    resultado.push(array.slice(i, i + tamanho));
  }
  return resultado;
}

const mockedColumns = [
  {
    name: "#",
    flex: 0.3,
    fontSize: 8,
  },
  {
    name: "Serviço",
    flex: 1.3,
    fontSize: 8,
  },
  {
    name: "Descrição",
    flex: 1.4,
    fontSize: 8,
  },
  // {
  //   name: "Entrada",
  //   flex: 1,
  // },
  // {
  //   name: "Saída",
  //   flex: 1,
  // },
  {
    name: "Qtd",
    flex: 0.5,
    fontSize: 8,
  },
  {
    name: "Diárias",
    flex: 0.6,
    fontSize: 8,
  },
  {
    name: "Valor un",
    separator: true,
    flex: 1,
    fontSize: 8,
  },
  // {
  //   name: "Total tarifa",
  //   flex: 1.1,
  // },
  {
    name: "Valor Total",
    flex: 1.1,
    color: "#EDEDED",
    fontSize: 8,
  },
  {
    name: "Total (R$)",
    flex: 1.1,
    separator: true,
    color: "#EDEDED",
    fontSize: 8,
  },
  {
    name: "Taxa de serviço (R$)",
    flex: 1,
    color: "#FFFFFF",
    fontSize: 8,
  },
  {
    name: "Taxa MM (Fee) (R$)",
    flex: 1,
    color: "#FFFFFF",
    fontSize: 8,
  },
  {
    name: "Markup (R$)",
    flex: 1,
    color: "#FFFFFF",
    fontSize: 8,
  },
  {
    name: "Over (R$)",
    flex: 1,
    color: "#FFFFFF",
    fontSize: 8,
  },
  {
    name: "Comissão (R$)",
    flex: 1,
    color: "#FFFFFF",
    fontSize: 8,
  },
  {
    name: "Incentivo (R$)",
    flex: 1,
    color: "#FFFFFF",
    fontSize: 8,
  },
  {
    name: "ISS (R$)",
    flex: 1,
    color: "#FFFFFF",
    fontSize: 8,
  },
  {
    name: "Imposto (R$)",
    flex: 1,
    color: "#FFFFFF",
    fontSize: 8,
  },
  {
    name: "Imposto local (R$)",
    flex: 1,
    color: "#FFFFFF",
    fontSize: 8,
  },
  {
    name: "Taxa de turismo (R$)",
    flex: 1,
    color: "#FFFFFF",
    fontSize: 8,
  },
  {
    name: "Câmbio",
    flex: 0.7,
    color: "#FFFFFF",
    fontSize: 8,
  },
  {
    name: "IOF",
    flex: 0.7,
    color: "#FFFFFF",
    fontSize: 8,
  },
  {
    name: "Total + taxas",
    flex: 1,
    color: "#FFFFFF",
    fontSize: 8,
  },
];

const mockedColumns2 = [
  {
    name: "1",
    flex: 0.3,
  },
  {
    name: "HOTEIS & RESORTS S/A - 98.765.432/0001-01",
    flex: 7,
  },
  {
    name: "10%",
    flex: 1,
    color: "#FFFFFF",
    fontSize: 9,
  },
  {
    name: "10%",
    flex: 1,
    color: "#FFFFFF",
    fontSize: 9,
  },
  {
    name: "10%",
    flex: 1,
    color: "#FFFFFF",
    fontSize: 9,
  },
  {
    name: "10%",
    flex: 1,
    color: "#FFFFFF",
    fontSize: 9,
  },
  {
    name: "10%",
    flex: 1,
    color: "#FFFFFF",
    fontSize: 9,
  },
  {
    name: "10%",
    flex: 1,
    color: "#FFFFFF",
    fontSize: 9,
  },
  {
    name: "10%",
    flex: 1,
    color: "#FFFFFF",
    fontSize: 9,
  },
  {
    name: "10%",
    flex: 1,
    color: "#FFFFFF",
    fontSize: 9,
  },
  {
    name: "10%",
    flex: 1,
    color: "#FFFFFF",
    fontSize: 9,
  },
  {
    name: "10%",
    flex: 1,
    color: "#FFFFFF",
    fontSize: 9,
  },
  {
    name: "",
    flex: 0.7,
    color: "#FFFFFF",
    fontSize: 9,
  },
  {
    name: "",
    flex: 0.7,
    color: "#FFFFFF",
    fontSize: 9,
  },
  {
    name: "",
    flex: 1,
    color: "#FFFFFF",
    fontSize: 9,
  },
];

const buildTaxLabel = (value: any, type: "PERCENTAGE" | "FIX_VALUE") => {
  if (type === "PERCENTAGE") {
    return value?.value + "%";
  } else {
    return formatCurrency(value?.valueBRL);
  }
};

function calculateDetailedSums(items: any): {
  taxes: { totalInBRL: number; totalInCurrency: number };
  totals: { totalInBRL: number; totalInCurrency: number };
} {
  return items?.reduce(
    (acc: any, item: any) => {
      acc.taxes.totalInBRL += item.taxes.totalInBRL;
      acc.taxes.totalInCurrency += item.taxes.totalInCurrency;

      acc.totals.totalInBRL += item.totals.totalInBRL;
      acc.totals.totalInCurrency += item.totals.totalInCurrency;

      return acc;
    },
    {
      taxes: { totalInBRL: 0, totalInCurrency: 0 },
      totals: { totalInBRL: 0, totalInCurrency: 0 },
    }
  );
}

export function ExportPageMM(props: any) {
  const budget = props.budgetData;
  const isNegotiate = props.isNegotiate;
  const isClosing = props?.isClosing;

  const client = props?.clientData;

  const formatBudget =
    !!budget?.length &&
    budget?.map((item: any, index: number) => {
      // const services = item?.budgetServices.filter(
      //   (service: any) => service?.calcOption === "A"
      // );
      const services = item?.budgetServices;
      const exchanges = item?.exchangeAndDescriptionByType;
      const budgetIndex = item?.budgetIndex;
      const budgetName = item?.name;
      const baseValues = item?.baseValues;

      const groupedServices = groupByAttributes(services, [
        "type",
        "calcOption",
        "supplierId",
      ]);

      const mappedServices = Object.entries(groupedServices).map(
        ([key, value]) => {
          return {
            key,
            items: value,
          };
        }
      );

      const formattedServices: any[] = mappedServices.map((m: any) => {
        const [type, calc, supplier] = m.key.split("|");

        const baseValue = baseValues?.find(
          (e: any) =>
            e.category === type &&
            e?.calcOption === calc &&
            e?.supplierId === supplier
        );

        const exchange = baseValue?.exchange;

        // const exchange = exchanges?.find(
        //   (e: any) =>
        //     e.type === type &&
        //     e?.calcOption === calc &&
        //     e?.supplierId === supplier
        // )?.exchangeData;

        // const exchange = isClosing
        //   ? m?.items?.[0]?.closingValues?.exchangeData
        //   : m?.items?.[0]?.budgetValues?.exchangeData;

        const isBRL = exchange?.currency === "BRL";

        const totals = item?.budgetValuesTotal?.byCalculationType?.[calc];

        const closingTotals = m?.items?.[0]?.closingValues?.totals;

        const itemTotal = totals?.[type];

        const currentTotalItems = itemTotal?.filter(
          (i: any) => i.supplierId === supplier
        );

        const typeTotals = calculateDetailedSums(currentTotalItems);

        const formattedItems = m.items.map((i: any, itemIndex: number) => {
          const values = isClosing ? i?.closingValues : i?.budgetValues;

          const taxData = {
            ...values.taxData,
            ...values.totals,
          };

          const items = ServiceOptions[type];
          const itemName = items.find(
            (i: any) => i.value === values.serviceType
          );
          const taxColumns = [
            {
              name: "",
              flex: 0.3,
            },
            {
              name: i?.supplierName,
              flex: 7,
            },
            {
              name: buildTaxLabel(
                taxData?.serviceTax,
                taxData?.serviceTax?.typeTax
              ),
              flex: 1,
              color: "#FFFFFF",
              fontSize: 9,
            },
            {
              name: buildTaxLabel(taxData?.fee, taxData?.fee?.typeTax),
              flex: 1,
              color: "#FFFFFF",
              fontSize: 9,
            },
            {
              name: buildTaxLabel(taxData?.markup, taxData?.markup?.typeTax),
              flex: 1,
              color: "#FFFFFF",
              fontSize: 9,
            },
            {
              name: buildTaxLabel(
                taxData?.overhead,
                taxData?.overhead?.typeTax
              ),
              flex: 1,
              color: "#FFFFFF",
              fontSize: 9,
            },
            {
              name: buildTaxLabel(
                taxData?.commission,
                taxData?.commission?.typeTax
              ),
              flex: 1,
              color: "#FFFFFF",
              fontSize: 9,
            },
            {
              name: buildTaxLabel(
                taxData?.incentive,
                taxData?.incentive?.typeTax
              ),
              flex: 1,
              color: "#FFFFFF",
              fontSize: 9,
            },
            {
              name: buildTaxLabel(taxData?.iss, taxData?.iss?.typeTax),
              flex: 1,
              color: "#FFFFFF",
              fontSize: 9,
            },
            {
              name: buildTaxLabel(taxData?.tribute, taxData?.tribute?.typeTax),
              flex: 1,
              color: "#FFFFFF",
              fontSize: 9,
            },
            {
              name: buildTaxLabel(
                taxData?.localTax,
                taxData?.localTax?.typeTax
              ),
              flex: 1,
              color: "#FFFFFF",
              fontSize: 9,
            },
            {
              name: buildTaxLabel(
                taxData?.tourismTax,
                taxData?.tourismTax?.typeTax
              ),
              flex: 1,
              color: "#FFFFFF",
              fontSize: 9,
            },
            {
              name: "",
              flex: 0.7,
              color: "#FFFFFF",
              fontSize: 9,
            },
            {
              name: "",
              flex: 0.7,
              color: "#FFFFFF",
              fontSize: 9,
            },
            {
              name: "",
              flex: 1,
              color: "#FFFFFF",
              fontSize: 9,
            },
          ];

          const row = [
            itemIndex + 1,
            itemName?.text,
            values.observation,
            // formatDate(values.inputDate),
            // formatDate(values.outDate),
            values.quantity,
            values.daily,
            `${getCurrencyDisplayLabel(exchange?.currency)} ${formatCurrency(taxData?.tariff?.value)}`,
            // formatCurrency(
            //   isBRL ? taxData?.tariff?.value : taxData?.tariff?.valueBRL
            // ),
            // formatCurrency(
            //   isBRL ? taxData?.tariff?.value : taxData?.tariff?.valueBRL
            // ),
            isBRL ? "-" : formatCurrency(taxData?.tariff?.fullCoinService),
            isBRL
              ? formatCurrency(taxData?.tariff?.totalServiceConverted)
              : formatCurrency(
                  taxData?.tariff?.fullCoinService * exchange?.value
                ),
            formatCurrency(taxData?.serviceTax?.valueBRL),
            formatCurrency(taxData?.fee?.valueBRL),
            formatCurrency(taxData?.markup?.valueBRL),
            formatCurrency(taxData?.overhead?.valueBRL),
            formatCurrency(taxData?.commission?.valueBRL),
            // formatCurrency(
            //   isBRL ? taxData?.iof?.value : taxData?.iof?.valueBRL
            // ),
            formatCurrency(taxData?.incentive?.valueBRL),
            formatCurrency(taxData?.iss?.valueBRL),
            formatCurrency(taxData?.tribute?.valueBRL),
            formatCurrency(taxData?.localTax?.valueBRL),
            formatCurrency(taxData?.tourismTax?.valueBRL),
            isBRL ? "-" : `${getCurrencyDisplayLabel(exchange?.currency)} ${formatExchangeValue(exchange?.currency, exchange?.value)}`,
            formatCurrency(taxData?.iof?.valueBRL),
            formatCurrency(taxData?.totalInBRL),
          ];

          return {
            row,
            taxes: taxColumns,
          };
        });

        const typeTag: keyof typeof BudgetItemTypeEnum = type;
        const typeName = BudgetItemTypeEnum[typeTag];

        const typeColumns = mockedColumns.map((item) => {
          if (item.name === "TotalCurrency") {
            return {
              ...item,
              name: isBRL ? "-" : `Total (${getCurrencyDisplayLabel(exchange?.currency)})`,
            };
          } else {
            return item;
          }
        });

        const costAndTariffTotals = calculateTotals(formattedItems);

        return {
          type: typeName,
          totals: typeTotals,
          items: formattedItems,
          columns: typeColumns,
          currency: exchange?.currency,
          currencyValue: exchange?.value,
          tariffTotal: formatCurrency(costAndTariffTotals.tariffTotal),
          tariffTotalNumber: costAndTariffTotals.tariffTotal,
          totalCostInCurrency: isBRL
            ? "-"
            : formatCurrency(costAndTariffTotals.totalCostInCurrency),
          totalCostInCurrencyNumber: isBRL
            ? 0
            : costAndTariffTotals.totalCostInCurrency,
          totalCostInBRL: formatCurrency(costAndTariffTotals.totalCostInBRL),
          totalCostInBRLNumber: costAndTariffTotals.totalCostInBRL,
          calcOption: calc,
          closingTotals,
        };
      });

      const formattedGroupedServices = groupByAttributes(formattedServices, [
        "calcOption",
      ]);

      const sumTotals: any[] = [];

      Object.entries(formattedGroupedServices).forEach(([key, value]: any) => {
        value.forEach((item: any) => sumTotals.push(item?.totals?.totals));
      });

      const globalCurrencyTotals = sumTotals.reduce((sum: any, item: any) => {
        return sum + item?.totalInCurrency;
      }, 0);

      const globalBRLTotals = sumTotals.reduce((sum: any, item: any) => {
        return sum + item?.totalInBRL;
      }, 0);

      return {
        name: `${budgetName}${
          isNegotiate ? ` - Negociação ${item?.negotiationVersion}` : ""
        }`,
        services: formattedServices,
        servicesGrouped: formattedGroupedServices,
        totals: {
          totalInCurrency: globalCurrencyTotals,
          totalInBRL: globalBRLTotals,
        },
        budgetIndex,
        budgetName: budgetName,
      };
    });

  const budgetTotals = formatBudget.map((b: any) => {
    const groupedServices = groupByAttributes(b?.services, ["type"]);

    const formattedServices = Object.entries(groupedServices).map(
      ([key, value]) => {
        const items: any = value;

        const tariffTotal = items?.reduce((sum: any, item: any) => {
          const valor = item?.tariffTotalNumber || 0;
          return sum + valor;
        }, 0);

        const totalTaxes = items?.reduce((sum: any, item: any) => {
          const valor = parseFloat(item?.totals?.taxes?.totalInCurrency) || 0;
          return sum + valor;
        }, 0);

        const totalCostInBRL = items?.reduce((sum: any, item: any) => {
          const valor = parseFloat(item?.totals?.totals?.totalInBRL) || 0;
          return sum + valor;
        }, 0);

        const totalCostInBRLClosing = items?.reduce((sum: any, item: any) => {
          const valor = parseFloat(item?.closingTotals?.totalInBRL) || 0;
          return sum + valor;
        }, 0);

        const totalCostInCurrency = items?.reduce((sum: any, item: any) => {
          const valor = parseFloat(item?.totalCostInCurrencyNumber) || 0;
          return sum + valor;
        }, 0);

        return {
          key,
          tariffTotal,
          totalTaxes,
          totalCostInBRL: isClosing ? totalCostInBRLClosing : totalCostInBRL,
          totalCostInCurrency,
          currency: items?.find((item: any) => item?.currency)?.currency,
          budgetIndex: b?.budgetIndex,
          hasService: !!b?.services?.length,
          budgetName: b?.budgetName,
        };
      }
    );

    if (!formattedServices.length) {
      return [{ budgetIndex: b?.budgetIndex, budgetName: b?.budgetName }];
    }

    return formattedServices;
  });

  const budgetTotalKeys = getUniqueKeys(budgetTotals);

  const dividedBudgetTotals = divideInPortions(budgetTotals);

  // const getBudgetTotalValues = (item: any, key: string, label: string) => {
  //   const keyLabels: any = {
  //     ["Total tarifa"]: "tariffTotal",
  //     ["Total Tax./Imp."]: "totalTaxes",
  //     ["Total (USD)"]: "totalCostInCurrency",
  //     ["Total (R$)"]: "totalCostInBRL",
  //   };

  //   const findItem = item?.find((i: any) => i.key === key);

  //   if (findItem) {
  //     return formatCurrency(findItem[keyLabels[label]]);
  //   } else {
  //     return "-";
  //   }
  // };

  function addLineBreaks(input: string): string {
    const parts: string[] = [];

    for (let i = 0; i < input.length; i += 8) {
      const part = input.substring(i, i + 8);
      parts.push(part);
    }

    return parts.join("\n");
  }

  function renderHeader() {
    return (
      <>
        <View style={styles.exportHeader}>
          <div style={{ ...styles.headerDiv, ...styles.backgroundBlack }}>
            <div style={styles.childHeaderDiv}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                {client?.logo_url && (
                  <Image
                    src={client.logo_url}
                    style={{ width: 50, height: 50, objectFit: "contain" }}
                  />
                )}
                <div>
                  <Text
                    style={{
                      ...styles.whiteText,
                      fontSize: 22,
                      fontWeight: "bold",
                    }}
                  >
                    {client?.name}
                  </Text>
                  <br />
                  <Text
                    style={{
                      ...styles.grayText,
                      fontSize: 22,
                      fontWeight: "bold",
                    }}
                  >
                    {client?.nickname}
                  </Text>
                </div>
              </div>
            </div>
          </div>
          <div style={{ ...styles.headerDiv, ...styles.backgroundGray }}>
            <div
              style={{
                ...styles.childHeaderDiv,
                ...styles.textSpan,
                ...styles.grayText,
              }}
            >
              <div style={{ padding: "8px 0" }}>
                <Text style={{ color: "#727272", fontSize: 14 }}>Evento</Text>
              </div>
              <Text style={{ color: "#272727" }}>{client.event.name}</Text>
              <div style={styles.textDiv}>
                <Text>Inicio: </Text>
                <Text style={{ color: "#272727" }}>
                  {format(new Date(client.event.startDate), "dd/MM/yyyy")}
                </Text>
              </div>
              <div style={styles.textDiv}>
                <Text>Término: </Text>
                <Text style={{ color: "#272727" }}>
                  {format(new Date(client.event.endDate), "dd/MM/yyyy")}
                </Text>
              </div>
              {isPfizerClient(client?.name) && (
                <div style={styles.textDiv}>
                  <div style={styles.textDiv}>
                    <Text>PFZ: </Text>
                    <Text>{client.event.pfzCode}</Text>
                  </div>
                  <div style={styles.textDiv}>
                    <Text> MAAP: </Text>
                    <Text>{client.event.mappCode}</Text>
                  </div>
                </div>
              )}
            </div>
            <div style={styles.childHeaderDiv2}>
              <div
                style={{
                  ...styles.textDiv,
                  padding: "10px 0",
                  alignItems: "flex-end",
                }}
              >
                <Text
                  style={{
                    ...styles.textSpan,
                    ...styles.grayText,
                  }}
                >
                  Nº MM{" "}
                </Text>
                <Text style={{ ...styles.textSpan, ...styles.grayText }}>
                  {client.mmCode}
                </Text>
              </div>

              <br />
              <Text>Atendimento</Text>
              <div style={styles.textDiv}>
                <Text style={{ ...styles.textSpan }}>Solicitante </Text>
                <Text style={{ ...styles.textSpan, ...styles.grayText }}>
                  {client.requester}
                </Text>
              </div>
              <div style={styles.textDiv}>
                <Text style={{ ...styles.textSpan }}>Atend/Comercial </Text>
                <Text style={{ ...styles.textSpan, ...styles.grayText }}>
                  {client.firstAttendance}
                </Text>
              </div>
              <div style={styles.textDiv}>
                <Text style={{ ...styles.textSpan }}>Coordenador </Text>
                <Text style={{ ...styles.textSpan, ...styles.grayText }}>
                  {client.commercial}
                </Text>
              </div>
            </div>
          </div>
        </View>
        <View style={styles.view}>
          <div style={{ width: "98%" }}>
            <Text style={{ ...styles.header2, fontWeight: "bold" }}>
              Proposta
            </Text>
            <Text
              style={{
                ...styles.header2,
                fontSize: "14px",
                ...styles.grayText,
                marginTop: "10px",
              }}
            >
              Comparações de {isNegotiate ? "negociações" : "orçamentos"}
            </Text>
            <Text
              style={{
                ...styles.header2,
                fontSize: "14px",
                ...styles.grayText,
                marginTop: "10px",
              }}
            >
              Moeda padrão: BRL (R$)
            </Text>
          </div>
        </View>
      </>
    );
  }

  return (
    <Page size="A3" orientation="landscape" style={styles.page}>
      {props.showHeader && renderHeader()}
      <View
        style={{
          display: "flex",
          flexDirection: "row",
          width: "98%",
          marginLeft: 12,
          marginRight: 12,
          marginTop: 8,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
          {formatBudget.map((budget: any) => {
            return (
              <>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    backgroundColor: "black",
                    alignItems: "center",
                  }}
                >
                  <Text style={{ color: "white", margin: 8, fontSize: 10 }}>
                    {budget?.name}
                  </Text>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                      margin: 8,
                    }}
                  >
                    <Text style={{ color: "white" }}>
                      {client?.event?.local}
                    </Text>
                    <Text
                      style={{
                        fontSize: 10,
                        maxWidth: 200,
                        color: "#C1C1C1",
                      }}
                    >
                      {client.event.neighborhood} {client.event.number},{" "}
                      {client.event.city}, {client.event.state},{" "}
                      {client.event.country}
                      {"\n"}
                      {format(new Date(client.event.startDate), "d MMMM yyyy", {
                        locale: ptBR,
                      })}{" "}
                      à{" "}
                      {format(new Date(client.event.endDate), "d MMMM yyyy", {
                        locale: ptBR,
                      })}
                    </Text>
                  </div>
                </div>
                {Object.entries(budget?.servicesGrouped).map(
                  ([key, value]: any) => {
                    const taxesColumns = value[0]?.items?.[0]?.taxes;
                    return (
                      <div>
                        {/* Category: A, B, C, D, etc */}
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            width: "100%",
                            backgroundColor: "#505050",
                            padding: "12px 4px",
                            border: "1px solid #444444",
                          }}
                        >
                          <Text
                            style={{
                              fontSize: 10,
                              color: "#FFFFFF",
                              marginLeft: 25,
                            }}
                          >
                            {CalcOptionLabels?.[key]}
                          </Text>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            backgroundColor: "#505050",
                          }}
                        >
                          {mockedColumns?.map((column: any) => (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                borderBottom: "1px solid #444444",
                                flex: column?.flex,
                                alignItems: "center",
                                borderRight: column?.separator
                                  ? "1px solid #444444"
                                  : "1px solid #444444",
                                backgroundColor: "#505050",
                              }}
                            >
                              <Text
                                style={{
                                  display: "flex",
                                  color: "#FFFFFF",
                                  fontSize: column.fontSize || 10,
                                  padding: 4,
                                }}
                              >
                                {column.name}
                              </Text>
                            </div>
                          ))}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            backgroundColor: "#747474",
                            padding: "4px 0px",
                          }}
                        >
                          {taxesColumns?.map((column: any) => (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                flex: column?.flex,
                                alignItems: "center",
                              }}
                            >
                              <Text
                                style={{
                                  display: "flex",
                                  color: "#FFFFFF",
                                  fontSize: column.fontSize || 10,
                                  padding: 4,
                                }}
                              >
                                {column.name}
                              </Text>
                            </div>
                          ))}
                        </div>
                        <View>
                          {value.map((v: any) => {
                            return (
                              <>
                                {/* Type: Hospedagem, A&B, etc */}
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    width: "100%",
                                    backgroundColor: "#B3B3B3",
                                    padding: 4,
                                  }}
                                >
                                  <Text
                                    style={{
                                      fontSize: 10,
                                      color: "#373737",
                                      marginLeft: 25,
                                    }}
                                  >
                                    {v?.type}
                                  </Text>
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    backgroundColor: "#F3F3F3",
                                    flexDirection: "row",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: "row",
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          flexDirection: "column",
                                          width: "100%",
                                        }}
                                      >
                                        {/* <div
                                          style={{
                                            display: "flex",
                                            flexDirection: "row",
                                          }}
                                        >
                                          {v?.columns?.map((column: any) => (
                                            <div
                                              style={{
                                                display: "flex",
                                                flexDirection: "row",
                                                borderBottom:
                                                  "1px solid #7C7C7C",
                                                flex: column?.flex,
                                                alignItems: "center",
                                                borderRight: column?.separator
                                                  ? "1px solid #7C7C7C"
                                                  : "none",
                                                backgroundColor:
                                                  column.color || "#F3F3F3",
                                              }}
                                            >
                                              <Text
                                                style={{
                                                  display: "flex",
                                                  color: "#7C7C7C",
                                                  fontSize:
                                                    column.fontSize || 10,
                                                  padding: 4,
                                                }}
                                              >
                                                {column.name}
                                              </Text>
                                            </div>
                                          ))}
                                        </div> */}

                                        {v?.items?.map((item: any) => (
                                          <div
                                            style={{
                                              display: "flex",
                                              flexDirection: "row",
                                            }}
                                          >
                                            {item?.row.map(
                                              (value: any, index: number) => (
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    borderBottom:
                                                      "1px solid #7C7C7C",
                                                    flex: mockedColumns[index]
                                                      ?.flex,
                                                    alignItems: "center",
                                                    borderRight: mockedColumns[
                                                      index
                                                    ]?.separator
                                                      ? "1px solid #7C7C7C"
                                                      : "none",
                                                    backgroundColor:
                                                      mockedColumns[index]
                                                        ?.color || "#F3F3F3",
                                                  }}
                                                >
                                                  <Text
                                                    style={{
                                                      display: "flex",
                                                      fontSize: 9,
                                                      padding: 4,
                                                    }}
                                                  >
                                                    {value}
                                                  </Text>
                                                </div>
                                              )
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            );
                          })}
                        </View>
                      </div>
                    );
                  }
                )}
                {/* <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      width: 669.5,
                      justifyContent: "space-between",
                      alignItems: "center",
                      borderRight: "1px solid #7C7C7C",
                      backgroundColor: "#F3F3F3",
                    }}
                  ></div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      backgroundColor: "#B3B3B3",
                      flex: 1,
                      justifyContent: "flex-end",
                      padding: 4,
                    }}
                  >
                    <Text
                      style={{
                        padding: 4,
                        fontSize: 10,
                        color: "#2F2F2F",
                      }}
                    >
                      Total moeda/convertido US$281.983,01 / R$1.406.915,05
                    </Text>
                  </div>
                </div> */}
              </>
            );
          })}
        </div>
      </View>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "98%",
          marginLeft: 12,
          marginRight: 12,
          marginTop: 32,
          marginBottom: 16,
        }}
      >
        <Text style={{ ...styles.header2, fontWeight: "bold" }}>Totais</Text>
        <Text
          style={{
            ...styles.header2,
            fontSize: "14px",
            ...styles.grayText,
            marginTop: "10px",
          }}
        >
          Totais do orçamentos
        </Text>
      </div>

      {dividedBudgetTotals.map((budgetItem: any, budgetItemIndex: number) => {
        const budgetServices = getUniqueKeys(budgetItem);

        return (
          <View
            style={{
              flexDirection: "column",
              flex: 1,
              width: "98%",
              marginLeft: 12,
              marginRight: 12,
            }}
            wrap={false}
          >
            <div style={{ flexDirection: "row" }}>
              <div style={{ flex: 1, flexDirection: "row", padding: 4 }}></div>
              {budgetItem?.map((i: any, index: number) => {
                return (
                  <div
                    style={{
                      flex: 1,
                      flexDirection: "row",
                      backgroundColor: "black",
                      padding: 4,
                    }}
                  >
                    <Text style={{ color: "white", fontSize: 10 }}>
                      {i?.[0]?.budgetName}
                    </Text>
                  </div>
                );
              })}
            </div>
            {budgetServices?.map((service) => {
              const totalsLabels: any = [
                { label: "Total tarifa" },
                { label: "Total Tax./Imp." },
              ];

              let currencies: any[] = [];

              budgetItem.forEach((b: any) => {
                b.forEach((i: any) => {
                  if (i?.currency && i?.currency !== "BRL") {
                    currencies.push(i?.currency);
                  }
                });
              });

              const uniqueCurrencies = getUniqueValues(currencies).map((c) => {
                return {
                  label: `Total (${c})`,
                  value: c,
                };
              });

              const allLabels: any = [
                // ...totalsLabels,
                // ...uniqueCurrencies,
                { label: "Total (R$)" },
              ];

              return (
                <>
                  {service && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        width: "100%",
                        backgroundColor: "#B3B3B3",
                        padding: 4,
                      }}
                    >
                      <Text style={{ fontSize: 10, color: "white" }}>
                        {service}
                      </Text>
                    </div>
                  )}
                  {service &&
                    allLabels.map((label: any) => (
                      <div
                        style={{
                          flexDirection: "row",
                          borderBottom: "1px solid #B3B3B3",
                        }}
                      >
                        <div
                          style={{ flex: 1, flexDirection: "row", padding: 4 }}
                        >
                          <Text style={{ fontSize: 10 }}>{label.label}</Text>
                        </div>
                        {budgetItem?.map((itemList: any) => {
                          const findItem = itemList?.find(
                            (i: any) => i.key === service
                          );

                          const foundCurrency =
                            findItem?.currency === label?.value
                              ? findItem?.totalCostInCurrency
                              : null;

                          const keyLabels: any = {
                            ["Total tarifa"]: "tariffTotal",
                            ["Total Tax./Imp."]: "totalTaxes",
                            ["Total (USD)"]: "totalCostInCurrency",
                            ["Total (R$)"]: "totalCostInBRL",
                          };

                          return (
                            <div
                              style={{
                                flex: 1,
                                flexDirection: "row",
                                padding: 4,
                                backgroundColor: "#F3F3F3",
                              }}
                            >
                              <Text style={{ fontSize: 10, color: "#373737" }}>
                                {formatCurrency(
                                  findItem?.[keyLabels[label.label]] ||
                                    foundCurrency
                                ) || "-"}
                              </Text>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                </>
              );
            })}
          </View>
        );
      })}
    </Page>
  );
}
